import React, { useState, useEffect } from "react";
import axios from "axios";
import "../styles/AdminDashboard.css";
import {
  FaShoppingCart,
  FaCalendarAlt,
  FaRupeeSign,
  FaChartLine,
  FaServer,
  FaEnvelope,
  FaCreditCard,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock
} from "react-icons/fa";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    products: [],
    workshops: [],
    orders: [],
    stats: {
      totalOrders: 0,
      totalRevenue: 0,
      pendingOrders: 0,
      completedOrders: 0,
      totalProducts: 0,
      totalWorkshops: 0,
      recentOrders: []
    },
    systemHealth: {
      database: 'unknown',
      email: 'unknown',
      payment: 'unknown',
      server: 'unknown'
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    // Refresh data every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel
      const [
        productsRes,
        workshopsRes,
        ordersRes,
        statsRes,
        healthRes
      ] = await Promise.allSettled([
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/workshops`),
        axios.get(`${API_URL}/api/admin/orders?limit=10`),
        axios.get(`${API_URL}/api/admin/analytics/summary`),
        axios.get(`${API_URL}/health`)
      ]);

      // Process results
      const products = productsRes.status === 'fulfilled' ? productsRes.value.data : [];
      const workshops = workshopsRes.status === 'fulfilled' ? workshopsRes.value.data : [];
      const orders = ordersRes.status === 'fulfilled' ? ordersRes.value.data.orders || [] : [];
      const stats = statsRes.status === 'fulfilled' ? statsRes.value.data : {};
      const health = healthRes.status === 'fulfilled' ? healthRes.value.data.services || {} : {};

      console.log('Dashboard data fetched:', {
        products: products.length,
        workshops: workshops.length,
        orders: orders.length,
        stats,
        health
      });

      setDashboardData({
        products,
        workshops,
        orders,
        stats: {
          totalOrders: stats.totalOrders || 0,
          totalRevenue: stats.totalRevenue || 0,
          pendingOrders: stats.orderStats?.find(s => s._id === 'processing')?.count || 0,
          completedOrders: stats.orderStats?.find(s => s._id === 'delivered')?.count || 0,
          totalProducts: products.length,
          totalWorkshops: workshops.length,
          recentOrders: stats.recentOrders || []
        },
        systemHealth: {
          database: health.firebase || 'unknown',
          email: health.email || 'unknown',
          payment: health.payment || 'unknown',
          server: 'available'
        }
      });

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError("Failed to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
      case 'configured':
        return <FaCheckCircle className="status-icon success" />;
      case 'not available':
      case 'not configured':
        return <FaExclamationTriangle className="status-icon warning" />;
      default:
        return <FaClock className="status-icon pending" />;
    }
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'delivered': return '#28a745';
      case 'shipped': return '#007bff';
      case 'confirmed': return '#ffc107';
      case 'processing': return '#17a2b8';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  if (loading && !dashboardData.products.length) {
    return (
      <div className="dashboard-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-state">
          <FaExclamationTriangle />
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <p>Monitor and manage your Godai Studios platform</p>
        <button onClick={fetchDashboardData} className="refresh-btn">
          {loading ? "Refreshing..." : "Refresh Data"}
        </button>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon">
            <FaShoppingCart />
          </div>
          <div className="metric-content">
            <h3>{dashboardData.stats.totalOrders}</h3>
            <p>Total Orders</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">
            <FaRupeeSign />
          </div>
          <div className="metric-content">
            <h3>₹{dashboardData.stats.totalRevenue.toLocaleString()}</h3>
            <p>Total Revenue</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">
            <FaClock />
          </div>
          <div className="metric-content">
            <h3>{dashboardData.stats.pendingOrders}</h3>
            <p>Pending Orders</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">
            <FaCheckCircle />
          </div>
          <div className="metric-content">
            <h3>{dashboardData.stats.completedOrders}</h3>
            <p>Completed Orders</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">
            <FaShoppingCart />
          </div>
          <div className="metric-content">
            <h3>{dashboardData.stats.totalProducts}</h3>
            <p>Products</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">
            <FaCalendarAlt />
          </div>
          <div className="metric-content">
            <h3>{dashboardData.stats.totalWorkshops}</h3>
            <p>Workshops</p>
          </div>
        </div>
      </div>

      {/* System Health */}
      <div className="system-health">
        <h2>System Health</h2>
        <div className="health-grid">
          <div className="health-item">
            <FaServer />
            <span>Database</span>
            {getStatusIcon(dashboardData.systemHealth.database)}
            <span className="status-text">{dashboardData.systemHealth.database}</span>
          </div>
          <div className="health-item">
            <FaEnvelope />
            <span>Email Service</span>
            {getStatusIcon(dashboardData.systemHealth.email)}
            <span className="status-text">{dashboardData.systemHealth.email}</span>
          </div>
          <div className="health-item">
            <FaCreditCard />
            <span>Payment Gateway</span>
            {getStatusIcon(dashboardData.systemHealth.payment)}
            <span className="status-text">{dashboardData.systemHealth.payment}</span>
          </div>
          <div className="health-item">
            <FaServer />
            <span>Server</span>
            {getStatusIcon(dashboardData.systemHealth.server)}
            <span className="status-text">{dashboardData.systemHealth.server}</span>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="recent-orders">
        <h2>Recent Orders</h2>
        <div className="orders-table">
          {dashboardData.stats.recentOrders.length === 0 ? (
            <p>No recent orders found.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Order Number</th>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.stats.recentOrders.map((order) => (
                  <tr key={order.id}>
                    <td>{order.orderNumber}</td>
                    <td>{order.userName}</td>
                    <td>₹{order.totalAmount?.toLocaleString()}</td>
                    <td>
                      <span 
                        className="status-badge"
                        style={{ backgroundColor: getOrderStatusColor(order.orderStatus) }}
                      >
                        {order.orderStatus}
                      </span>
                    </td>
                    <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <a href="/orders" className="action-card">
            <FaShoppingCart />
            <h3>Manage Orders</h3>
            <p>View and update order status</p>
          </a>
          <a href="/product" className="action-card">
            <FaShoppingCart />
            <h3>Manage Products</h3>
            <p>Add, edit, or remove products</p>
          </a>
          <a href="/workshops" className="action-card">
            <FaCalendarAlt />
            <h3>Manage Workshops</h3>
            <p>Create and manage workshops</p>
          </a>
          <a href="/analytics" className="action-card">
            <FaChartLine />
            <h3>View Analytics</h3>
            <p>Sales reports and insights</p>
          </a>
        </div>
      </div>

      {/* Preview Sections */}
      <div className="dashboard-previews">
        {/* Products Preview */}
        <section className="preview-section">
          <div className="section-header">
            <h3>Recent Products</h3>
            <a href="/product" className="view-all-btn">View All</a>
          </div>
          {dashboardData.products.length === 0 ? (
            <p>No products available.</p>
          ) : (
            <div className="product-grid">
              {dashboardData.products.slice(0, 4).map((product) => (
                <div key={product.id || product._id} className="product-card">
                  <img 
                    src={product.imageUrl?.[0] || product.imageUrl || 'https://via.placeholder.com/300x200?text=No+Image'} 
                    alt={product.name} 
                    className="product-image"
                    onError={(e) => { 
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/300x200?text=No+Product+Image"; 
                    }} 
                  />
                  <div className="product-info">
                    <h4>{product.name}</h4>
                    <p className="price">₹{product.price}</p>
                    <p className="stock">Stock: {product.stock || 0}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Workshops Preview */}
        <section className="preview-section">
          <div className="section-header">
            <h3>Upcoming Workshops</h3>
            <a href="/admin/workshops" className="view-all-btn">View All</a>
          </div>
          {dashboardData.workshops.length === 0 ? (
            <p>No workshops available.</p>
          ) : (
            <div className="workshop-grid">
              {dashboardData.workshops.slice(0, 4).map((workshop) => (
                <div key={workshop.id || workshop._id} className="workshop-card">
                  <img 
                    src={workshop.imageUrl || workshop.primaryImage || 'https://via.placeholder.com/300x200?text=No+Image'} 
                    alt={workshop.name} 
                    className="workshop-image"
                    onError={(e) => { 
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/300x200?text=No+Image"; 
                    }} 
                  />
                  <div className="workshop-info">
                    <h4>{workshop.name}</h4>
                    <p><strong>Date:</strong> {new Date(workshop.date).toLocaleDateString()}</p>
                    <p><strong>Time:</strong> {workshop.time}</p>
                    <p><strong>Available Slots:</strong> {workshop.slotsAvailable}</p>
                    <p className="price">₹{workshop.price}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;